import type { Scene, SubtitleSettings } from '@/types';
import type { VideoGenerationParams, VideoGenerationResult, SceneWithBlobData } from '@/types/application';
import { logger } from '@/lib/logger';
import { loadSubtitleFont } from '@/lib/fontLoader';
import { generateVideoWithWebCodecs } from '@/infrastructure/webcodecs/WebCodecsVideoGenerator';
import { VideoGenerationError } from '@/application/errors/VideoGenerationError';
import { deliverVideo } from '@/lib/videoDelivery';

// WebCodecs APIが利用可能かどうかをチェック
// 静止画からの生成にデコーダーは要らず、AACはネイティブのAudioEncoderが無ければ
// Worker側でWASMにフォールバックするため、VideoEncoderの有無だけを条件にする
function isWebCodecsAvailable(): boolean {
  return typeof VideoEncoder !== 'undefined';
}

async function detectImageSize(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      logger.error('画像サイズの検出に失敗しました', new Error('Image load failed'), {
        blobType: blob.type,
        blobSize: blob.size,
      });
      URL.revokeObjectURL(img.src);
      reject(new Error('画像の読み込みに失敗しました'));
    };
    img.src = URL.createObjectURL(blob);
  });
}

export class VideoGenerationService {
  private isCancelled: boolean = false;
  private currentWorker: Worker | null = null;

  async generateVideo(params: VideoGenerationParams): Promise<VideoGenerationResult> {
    this.isCancelled = false;
    this.currentWorker = null;
    const { scenes, videoSettings, subtitleSettings, globalSubtitleEnabled, onProgress } = params;

    // WebCodecs APIの利用可能性をチェック
    if (!isWebCodecsAvailable()) {
      throw new VideoGenerationError('WEBCODECS_UNSUPPORTED', 'VideoEncoder is not available in this browser');
    }

    this.validateScenesForVideo(scenes);

    const warnings: string[] = [];

    logger.info('動画生成開始', {
      sceneCount: scenes.length,
      subtitleEnabled: globalSubtitleEnabled,
    });

    // テロップが有効な場合、必要なフォントを事前ロード
    if (globalSubtitleEnabled && subtitleSettings) {
      const fontsToLoad = new Set<string>();
      fontsToLoad.add(subtitleSettings.defaultStyle.fontFamily);

      // シーンごとのカスタムフォントも収集
      scenes.forEach((scene) => {
        if (scene.subtitleEnabled && scene.subtitleStyle?.fontFamily) {
          fontsToLoad.add(scene.subtitleStyle.fontFamily);
        }
      });

      onProgress?.('フォントを読み込み中...', 5);

      for (const fontFamily of fontsToLoad) {
        try {
          await loadSubtitleFont(fontFamily as SubtitleSettings['defaultStyle']['fontFamily']);
        } catch (error) {
          const warningMsg = `フォント「${fontFamily}」の読み込みに失敗しました。代替フォントを使用します。`;
          logger.warn(warningMsg, { fontFamily, error });
          warnings.push(warningMsg);
        }
      }
    }

    // Blob URLをBlobに変換し、画像サイズも検出（Worker内でアクセス可能にするため）
    const scenesWithBlobs = await Promise.all(
      scenes.map(async (scene, index) => {
        onProgress?.(`画像を準備中... (${index + 1}/${scenes.length})`, 10 + (index / scenes.length) * 10);

        const imageResponse = await fetch(scene.imageUrl);
        if (!imageResponse.ok) {
          throw new Error(`画像の取得に失敗しました: シーン${index + 1} (HTTP ${imageResponse.status})`);
        }
        const imageBlob = await imageResponse.blob();
        const imageSize = await detectImageSize(imageBlob);
        const imageArrayBuffer = await imageBlob.arrayBuffer();

        let audioArrayBuffer: ArrayBuffer | null = null;
        if (scene.audioUrl) {
          const audioResponse = await fetch(scene.audioUrl);
          if (!audioResponse.ok) {
            throw new Error(`音声の取得に失敗しました: シーン${index + 1} (HTTP ${audioResponse.status})`);
          }
          const audioBlob = await audioResponse.blob();
          audioArrayBuffer = await audioBlob.arrayBuffer();
        }

        return {
          ...scene,
          imageData: imageArrayBuffer,
          imageWidth: imageSize.width,
          imageHeight: imageSize.height,
          audioData: audioArrayBuffer,
        };
      })
    );

    // キャンセルチェック
    if (this.isCancelled) {
      throw new DOMException('Video generation cancelled', 'AbortError');
    }

    logger.info('WebCodecs + Mediabunny エンジンを使用', {
      sceneCount: scenes.length,
    });

    const result = await generateVideoWithWebCodecs({
      scenes: scenesWithBlobs as SceneWithBlobData[],
      videoSettings,
      subtitleSettings,
      globalSubtitleEnabled,
      onProgress,
      onWorkerCreated: (worker) => {
        this.currentWorker = worker;
      },
    });

    const videoBlob = result.videoBlob;
    warnings.push(...result.warnings);

    logger.info('動画生成完了', { sizeKB: Math.round(videoBlob.size / 1024), warningCount: warnings.length });

    return { videoBlob, warnings };
  }

  /**
   * 動画生成をキャンセルする
   */
  cancelGeneration(): void {
    this.isCancelled = true;
    if (this.currentWorker) {
      this.currentWorker.terminate();
      this.currentWorker = null;
      logger.info('動画生成をキャンセルしました');
    } else {
      logger.info('動画生成をキャンセルしています...');
    }
  }

  validateScenesForVideo(scenes: Scene[]): void {
    if (scenes.length === 0) {
      throw new Error('シーンが1つも登録されていません');
    }

    const missingAudio = scenes.filter((s) => s.audioStatus !== 'done');
    if (missingAudio.length > 0) {
      throw new Error(
        `${missingAudio.length}個のシーンに音声が設定されていません`
      );
    }
  }

  /**
   * 生成した動画をユーザーの手元へ渡す。タッチ端末では共有シート、
   * それ以外は直接ダウンロード（ADR-0010）。経路の判定は videoDelivery が持つ。
   *
   * 共有はタップのハンドラから同期に呼ぶ必要があるため、この関数は async にしない。
   */
  deliverVideo(videoBlob: Blob): void {
    deliverVideo(videoBlob);
  }
}
